import { DateTime } from 'luxon';
import axios from '../axios';
import * as models from '../models';
import * as phone from '../phone';
import { G } from '../querystring';
import * as uploads from '../uploads';
import { GetPageRequestID } from './pagerequests';

declare interface quoteRequest {
    name: string;
    companyName: string;
    country: string;
    email: string;
    phone: string;
    product: string;
    type: string;
    size: string;
    dateNeeded: string;
    notes: string;
    quantity: number;
    childLineItems: {
        item: string;
        option: string;
    }[];
}

declare interface quoteRequestResp {
    quoteRequest: quoteRequest;
}

export async function GetQuoteRequest(id = G().Get('id') ?? ''): Promise<quoteRequest> {
    return axios.get<quoteRequestResp>(`/quoterequests/${id}`).then(resp => resp.data.quoteRequest);
}

interface newQuoteRequestOpts {
    name: string;
    company: string;
    phone: phone.Phone;
    email: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    stateID?: number;
    zip?: string;
    countryID?: number;
    lineItem?: string;
    type?: string;
    size?: string;
    width?: number;
    height?: number;
    children?: Child[];
    quantity?: number;
    budget?: number;
    promo?: string;
    project?: string;
    dateNeeded?: string;
    notes?: string;
    extras?: Extra[];
    uploader?: uploads.Uploader;
    uploadIDs?: number[];
    form?: string;
    sms?: boolean;
}

export interface Child {
    child: string;
    option?: string;
    quantity?: number;
}

export interface Extra {
    name: string;
    value?: string;
}

declare interface newQuoteRequestReq {
    name: string;
    company: string;
    phone: string;
    email: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    stateID?: number;
    zip?: string;
    countryID?: number;
    lineItem?: string;
    type?: string;
    size?: string;
    width?: number;
    height?: number;
    children?: Child[];
    quantity?: number;
    budget?: number;
    promo?: string;
    sms?: boolean;
    project?: string;
    dateNeeded?: string;
    notes?: string;
    extras?: Extra[];
    uploadIDs?: number[];
    form?: string;
    referrer: string;
    pageRequestID: string;
    tz: string;
}

declare interface newQuoteRequestResp {
    quoteRequest: models.QuoteRequest;
}

export async function NewQuoteRequest(opts: newQuoteRequestOpts): Promise<models.QuoteRequest> {
    const data: newQuoteRequestReq = {
        name: opts.name,
        company: opts.company,
        phone: await opts.phone.Format(),
        email: opts.email,
        addressLine1: opts.addressLine1,
        addressLine2: opts.addressLine2,
        city: opts.city,
        stateID: opts.stateID,
        zip: opts.zip,
        countryID: opts.countryID,
        lineItem: opts.lineItem,
        type: opts.type,
        size: opts.size,
        width: opts.width,
        height: opts.height,
        budget: opts.budget,
        promo: opts.promo,
        sms: opts.sms,
        project: opts.project,
        notes: opts.notes,
        form: opts.form,
        referrer: location.href,
        pageRequestID: await GetPageRequestID(),
        tz: new Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (opts.children?.length) {
        for (const e of opts.children) {
            if (!e.child) {
                continue;
            }

            const child: Child = {
                child: e.child,
            };

            if (e.option) {
                child.option = e.option;
            }

            if (e.quantity) {
                child.quantity = e.quantity;
            }

            if (!data.children) {
                data.children = [];
            }

            data.children.push(child);
        }
    }

    if (opts.extras?.length) {
        for (const e of opts.extras) {
            if (!e.name) {
                continue;
            }

            const extra: Extra = {
                name: e.name,
            };

            if (e.value) {
                extra.value = e.value;
            }

            if (!data.extras) {
                data.extras = [];
            }

            data.extras.push(extra);
        }
    }

    if (opts.dateNeeded) {
        const d = DateTime.fromISO(opts.dateNeeded);
        if (d.isValid) {
            data['dateNeeded'] = d.toISODate() ?? '';
        } else {
            if (!data['extras']) {
                data['extras'] = [];
            }
            data['extras'].push({
                'name': 'Date Needed',
                'value': opts.dateNeeded,
            });
        }
    }

    if (opts.quantity) {
        data['quantity'] = opts.quantity;
    }

    if (opts.uploader || opts.uploadIDs) {
        data['uploadIDs'] = [];
        if (opts.uploader) {
            data['uploadIDs'].push(...opts.uploader.GetUploadIDs());
        }

        if (opts.uploadIDs) {
            data['uploadIDs'].push(...opts.uploadIDs);
        }
    }

    return axios.post<newQuoteRequestResp>('/quoterequests', data).then(resp => resp.data.quoteRequest);
}